import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { captureUtmParams } from "@/lib/utm";

// Capture UTM params immediately on app load (before any routing)
captureUtmParams();
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { CookieConsent } from "@/components/CookieConsent";
import ErrorBoundary from "@/components/ErrorBoundary";
import ScrollToTop from "@/components/ScrollToTop";

// Eagerly load the landing page (critical path)
import Index from "./pages/Index";
import LocalizedIndex from "./pages/LocalizedIndex";

// Lazy-load all other pages to reduce initial bundle size
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Recalibrate = lazy(() => import("./pages/Recalibrate"));
const History = lazy(() => import("./pages/History"));
const SavedInsights = lazy(() => import("./pages/SavedInsights"));
const Profiles = lazy(() => import("./pages/Profiles"));
const UpwardTargets = lazy(() => import("./pages/UpwardTargets"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const SecurityPage = lazy(() => import("./pages/SecurityPage"));
const CompanyContextPage = lazy(() => import("./pages/CompanyContext"));
const TrustCenter = lazy(() => import("./pages/TrustCenter"));
const OrgAuditLog = lazy(() => import("./pages/OrgAuditLog"));
const TeamSeats = lazy(() => import("./pages/TeamSeats"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CoachingKnowledge = lazy(() => import("./pages/CoachingKnowledge"));
const Referral = lazy(() => import("./pages/Referral"));
const Pricing = lazy(() => import("./pages/Pricing"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const NotFound = lazy(() => import("./pages/NotFound"));
const EnterprisePage = lazy(() => import("./pages/EnterprisePage"));
const PainPage = lazy(() => import("./pages/PainPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const ForHrPage = lazy(() => import("./pages/ForHrPage"));
const ForMunicipalityPage = lazy(() => import("./pages/ForMunicipalityPage"));
const ForItPage = lazy(() => import("./pages/ForItPage"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const EscalationPage = lazy(() => import("./pages/EscalationPage"));
const AdminGrants = lazy(() => import("./pages/AdminGrants"));
const AdminFeedback = lazy(() => import("./pages/AdminFeedback"));
const AdminFunnel = lazy(() => import("./pages/AdminFunnel"));
const AdminAeoVisibility = lazy(() => import("./pages/AdminAeoVisibility"));
const AdminContent = lazy(() => import("./pages/AdminContent"));
const AdminKnowledge = lazy(() => import("./pages/AdminKnowledge"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminDaily = lazy(() => import("./pages/AdminDaily"));
const AdminRevenue = lazy(() => import("./pages/AdminRevenue"));
const AdminHealth = lazy(() => import("./pages/AdminHealth"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const AdminAiBias = lazy(() => import("./pages/AdminAiBias"));
const AdminAbuse = lazy(() => import("./pages/AdminAbuse"));
const AdminValidationSprint = lazy(() => import("./pages/AdminValidationSprint"));
const AdminFounderHq = lazy(() => import("./pages/AdminFounderHq"));
const AdminIdeas = lazy(() => import("./pages/AdminIdeas"));
const AdminIdeaCompetitors = lazy(() => import("./pages/AdminIdeaCompetitors"));
const AdminScoreSimulator = lazy(() => import("./pages/AdminScoreSimulator"));
const AdminOperations = lazy(() => import("./pages/AdminOperations"));
const AdminRealWorld = lazy(() => import("./pages/AdminRealWorld"));
const LeadMagnetConfirmed = lazy(() => import("./pages/LeadMagnetConfirmed"));
const ExpertReviewPage = lazy(() => import("./pages/ExpertReviewPage"));
const EmployeeTransparency = lazy(() => import("./pages/EmployeeTransparency"));
const BehavioralDrift = lazy(() => import("./pages/BehavioralDrift"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const ManagerBaseline = lazy(() => import("./pages/ManagerBaseline"));
const SciencePage = lazy(() => import("./pages/SciencePage"));
const MeetingsPage = lazy(() => import("./pages/MeetingsPage"));
const EmployeeOnboard = lazy(() => import("./pages/EmployeeOnboard"));
const WedgeWithdrawnEmployee = lazy(() => import("./pages/WedgeWithdrawnEmployee"));
const WaitlistContentConversionOs = lazy(() => import("./pages/WaitlistContentConversionOs"));
const WaitlistThanks = lazy(() => import("./pages/WaitlistThanks"));
const DiscoverySurvey = lazy(() => import("./pages/DiscoverySurvey"));
const Foerste100 = lazy(() => import("./pages/Foerste100"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <ErrorBoundary>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <Routes>
                <Route path="/" element={<Index />} />
                {/* Language-specific landing routes for SEO (hreflang targets) */}
                <Route path="/en" element={<LocalizedIndex lang="en" />} />
                <Route path="/no" element={<LocalizedIndex lang="nb" />} />
                <Route path="/sv" element={<LocalizedIndex lang="sv" />} />
                <Route path="/de" element={<LocalizedIndex lang="de" />} />
                <Route path="/fr" element={<LocalizedIndex lang="fr" />} />
                <Route path="/es" element={<LocalizedIndex lang="es" />} />
                <Route path="/pt" element={<LocalizedIndex lang="pt" />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/recalibrate" element={<Recalibrate />} />
                <Route path="/history" element={<History />} />
                <Route path="/saved" element={<SavedInsights />} />
                <Route path="/profiles" element={<Profiles />} />
                <Route path="/upward" element={<UpwardTargets />} />
                <Route path="/drift" element={<BehavioralDrift />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/baseline" element={<ManagerBaseline />} />
                <Route path="/meetings" element={<MeetingsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/security" element={<SecurityPage />} />
                <Route path="/settings/company-context" element={<CompanyContextPage />} />
                <Route path="/settings/audit" element={<OrgAuditLog />} />
                <Route path="/settings/team" element={<TeamSeats />} />
                <Route path="/trust" element={<TrustCenter />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/coaching-knowledge" element={<CoachingKnowledge />} />
                <Route path="/referral" element={<Referral />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/dashboard/billing" element={<Pricing />} />
                <Route path="/checkout/return" element={<CheckoutReturn />} />
                <Route path="/enterprise" element={<EnterprisePage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/science" element={<SciencePage />} />
                <Route path="/for-hr" element={<ForHrPage />} />
                <Route path="/for-kommune" element={<ForMunicipalityPage />} />
                <Route path="/for-it" element={<ForItPage />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/email-unsubscribe" element={<Unsubscribe />} />
                <Route path="/escalation" element={<EscalationPage />} />
                <Route path="/admin/grants" element={<AdminGrants />} />
                <Route path="/admin/feedback" element={<AdminFeedback />} />
                <Route path="/admin/funnel" element={<AdminFunnel />} />
                <Route path="/admin/aeo" element={<AdminAeoVisibility />} />
                <Route path="/admin/content" element={<AdminContent />} />
                <Route path="/admin/knowledge" element={<AdminKnowledge />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin" element={<AdminDaily />} />
                <Route path="/admin/revenue" element={<AdminRevenue />} />
                <Route path="/admin/health" element={<AdminHealth />} />
                <Route path="/admin/leads" element={<AdminLeads />} />
                <Route path="/admin/audit" element={<AdminAuditLog />} />
                <Route path="/admin/ai-bias" element={<AdminAiBias />} />
                <Route path="/admin/abuse" element={<AdminAbuse />} />
                <Route path="/admin/validation-sprint" element={<AdminValidationSprint />} />
                <Route path="/admin/founder-hq" element={<AdminFounderHq />} />
                <Route path="/admin/ideas" element={<AdminIdeas />} />
                <Route path="/admin/ideas/:id/competitors" element={<AdminIdeaCompetitors />} />
                <Route path="/admin/ideas/score-simulator" element={<AdminScoreSimulator />} />
                <Route path="/admin/operations" element={<AdminOperations />} />
                <Route path="/admin/real-world" element={<AdminRealWorld />} />
                <Route path="/waitlist/content-conversion-os" element={<WaitlistContentConversionOs />} />
                <Route path="/waitlist/thanks" element={<WaitlistThanks />} />
                <Route path="/discovery/survey/:ideaId" element={<DiscoverySurvey />} />
                <Route path="/foerste-100" element={<Foerste100 />} />
                <Route path="/lead-magnet/confirmed" element={<LeadMagnetConfirmed />} />
                <Route path="/expert-review/:token" element={<ExpertReviewPage />} />
                <Route path="/employee-transparency" element={<EmployeeTransparency />} />
                <Route path="/onboard/employee/:token" element={<EmployeeOnboard />} />
                <Route path="/svaersamtale" element={<WedgeWithdrawnEmployee />} />
                <Route path="/withdrawn-employee" element={<WedgeWithdrawnEmployee />} />
                <Route path="/:slug" element={<PainPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
          <CookieConsent />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
